// SPDX-License-Identifier: Elastic-2.0
/**
 * SuggestionCard — full vertical card for one pending suggestion (T-022).
 *
 * Productionised port of `SuggestionCard` from
 * `mirlai-os/design-system/ui_kits/dashboard/DashboardWidgets.jsx`
 * (lines 81-160). Three visual states:
 *
 *  1. Pending  — eyebrow, body, optional detail, action row (Yes / Not now / More info).
 *  2. Accepted — sage-green wash + "Learned." confirmation line. Action row hidden.
 *  3. Dismissed — muted (opacity 0.5) + "Dismissed · I can ask you again later." line.
 *
 * Reused by:
 *  - The standalone Suggestions page (T-027) — full vertical card stack.
 *  - `<SuggestionStrip />` reuses it for the dashboard hero (T-022).
 *
 * All copy is i18n-keyed (no hard-coded Spanish). The confidence eyebrow
 * is computed from `suggestion.confidence` via `confidenceLabelKey()`.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { Suggestion } from "@/api/gateway/models/Suggestion";
import { MirlaiMark } from "@/components/MirlaiMark/MirlaiMark";
import { Button } from "@/components/Button";
import { formatRelativeTime } from "@/lib/format";

import { confidenceLabelKey } from "./useSuggestions";
import styles from "./SuggestionCard.module.css";

export interface SuggestionCardProps {
  /** The suggestion to render. `description` is surfaced verbatim. */
  suggestion: Suggestion;
  /** "Yes, do this" — typically opens the approval modal (T-025). */
  onAccept: () => void;
  /** "Not now" — typically fires the dismiss mutation. */
  onDismiss: () => void;
  /**
   * Optional "More info →" — when omitted the button is hidden
   * (no-op flows: the strip on the dashboard skips it in compact mode).
   */
  onMoreInfo?: () => void;
  /** Optional secondary detail line below the main body. */
  detail?: string;
  /** Render the accepted (sage-green) state instead of the action row. */
  accepted?: boolean;
  /** Render the dismissed (muted) state instead of the action row. */
  dismissed?: boolean;
  /** Disable the action buttons (e.g. while a mutation is pending). */
  busy?: boolean;
}

/**
 * Standalone card used in the Suggestions list page (T-027) and reused
 * by `<SuggestionStrip />` as the dashboard hero.
 */
export function SuggestionCard({
  suggestion,
  onAccept,
  onDismiss,
  onMoreInfo,
  detail,
  accepted = false,
  dismissed = false,
  busy = false,
}: SuggestionCardProps): ReactElement {
  const { t } = useTranslation();
  const settled = accepted || dismissed;

  const eyebrowKey = confidenceLabelKey(suggestion.confidence);
  const confidenceLabel = t(eyebrowKey);
  const stateClass = accepted
    ? styles.accepted
    : dismissed
      ? styles.dismissed
      : styles.pending;

  return (
    <article
      className={[styles.card, stateClass].filter(Boolean).join(" ")}
      data-testid="suggestion-card"
      data-state={accepted ? "accepted" : dismissed ? "dismissed" : "pending"}
      aria-busy={busy || undefined}
    >
      <header className={styles.header}>
        <MirlaiMark size={14} />
        <span className={styles.eyebrow}>
          {t("suggestions.mirlai_noticed")} · {confidenceLabel}
        </span>
        <time className={styles.timestamp} dateTime={suggestion.created_at}>
          {formatRelativeTime(suggestion.created_at)}
        </time>
      </header>

      <p className={styles.body}>{suggestion.description}</p>

      {detail ? <p className={styles.detail}>{detail}</p> : null}

      {!settled ? (
        <div className={styles.actions} data-testid="suggestion-actions">
          <Button
            variant="primary"
            size="sm"
            onClick={onAccept}
            disabled={busy}
            data-testid="suggestion-accept"
          >
            {t("suggestions.actions.yes")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onDismiss}
            disabled={busy}
            data-testid="suggestion-dismiss"
          >
            {t("suggestions.actions.not_now")}
          </Button>
          {onMoreInfo ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMoreInfo}
              disabled={busy}
              className={styles.moreInfo}
              data-testid="suggestion-more-info"
            >
              {t("suggestions.actions.more_info")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {accepted ? (
        <p className={styles.acceptedNote} data-testid="suggestion-accepted-note">
          {t("suggestions.states.accepted")}
        </p>
      ) : null}

      {dismissed ? (
        <p className={styles.dismissedNote} data-testid="suggestion-dismissed-note">
          {t("suggestions.states.dismissed")}
        </p>
      ) : null}
    </article>
  );
}
